<template>
    <main class="linea-historia avanzado" :class="{ 'sin-scroll': modoAccion === 'historia-clinica' }">
        <!-- Vista especial para Historia Clínica Completa -->
        <div v-if="modoAccion === 'historia-clinica'" class="historia-clinica-container">
            <div class="historia-header">
                <h3><i class="fas fa-file-medical-alt"></i> {{ currentHistoryTitle }}</h3>

                <div class="header-buttons">
                    <button
class="search-toggle-btn" :class="{ 'active': showSearch }" title="Buscar sección"
                        @click="toggleSearch">
                        <i class="fas fa-search"></i>
                    </button>
                    <button
class="pdf-btn" :class="{ 'loading': generatingPdf }" :disabled="generatingPdf"
                        title="Generar PDF de Historia Clínica" @click="generateClinicalHistoryPdf">
                        <i class="fas" :class="generatingPdf ? 'fa-spinner fa-spin' : 'fa-file-pdf'"></i>
                    </button>
                    <button
class="cerrar-btn" title="Cerrar Historia Clínica"
                        @click="cerrarHistoriaClinica">&times;</button>
                </div>
            </div>

            <!-- Tabs de especialidades (solo si el médico tiene más de una) -->
            <div v-if="hasMultipleSpecialties" class="specialty-tabs">
                <button
v-for="spec in doctorSpecialties" :key="spec.id" class="specialty-tab-btn"
                    :class="{ active: Number(activeSpecialtyId) === Number(spec.id) }"
                    @click="activeSpecialtyId = spec.id">
                    <i class="fas fa-stethoscope me-2"></i>{{ getSpecialtyLabel(spec) }}
                </button>
            </div>

            <!-- Buscador de sección en Historia Clínica (centrado) -->
            <div v-if="showSearch" class="historia-search-wrapper">
                <div class="historia-search">
                    <SearchFilterClose
v-model="historiaSearch" placeholder="Buscar sección..."
                        @clear="clearHistoriaSearch" @focus="showSectionDropdown = true" />

                    <!-- Dropdown con lista de secciones -->
                    <Transition name="dropdown-fade">
                        <div v-if="showSectionDropdown" class="sections-dropdown">
                            <div class="dropdown-header">
                                <span class="dropdown-title">
                                    <template v-if="historiaSearch && historiaSearch.length >= 2">
                                        {{ filteredSections.length }} resultado{{ filteredSections.length !== 1 ? 's' :
                                            '' }}
                                    </template>
                                    <template v-else>
                                        Secciones disponibles ({{ availableSections.length }})
                                    </template>
                                </span>
                                <button class="close-dropdown" @click="showSectionDropdown = false">
                                    <i class="fas fa-times"></i>
                                </button>
                            </div>
                            <div v-if="filteredSections.length > 0" class="sections-list">
                                <button
v-for="section in filteredSections" :key="section" class="section-item"
                                    @click="navigateToSection(section)">
                                    <i class="fas fa-chevron-right"></i>
                                    <span>{{ section }}</span>
                                </button>
                            </div>
                            <div v-else class="no-results">
                                <i class="fas fa-search"></i>
                                <p>No se encontraron secciones con "{{ historiaSearch }}"</p>
                            </div>
                        </div>
                    </Transition>
                </div>
            </div>

            <div ref="historiaBodyRef" class="historia-body">
                <Transition name="fade-slide" mode="out-in">
                    <div :key="patientId" class="historia-content">
                        <!-- ✅ Un solo componente que maneja todas las especialidades -->
                        <PatientHC
v-if="patientRecord && patientRecord.id" ref="patientHCRef"
                            :patient-record="patientRecord"
                            :active-specialty-id="hasMultipleSpecialties ? activeSpecialtyId : null" />
                        <div v-else class="loading-message">
                            <p>Cargando historia clínica...</p>
                        </div>
                    </div>
                </Transition>
            </div>
        </div>

        <!-- Vista normal de Timeline -->
        <div v-else class="timeline-container avanzado">
            <!-- Controles de filtro (búsqueda y fecha) -->
            <div class="timeline-filters avanzado">
                <div class="filters-row filters-all-in-one">
                    <div class="filter-search">
                        <SearchFilterClose
v-model="historySearch"
                            placeholder="Buscar en todo el historial (cualquier campo)" @clear="clearAllFilters" />
                    </div>

                    <button
class="filter-btn" :class="{ 'active': typeFilter === 'all' }"
                        title="Mostrar todos los documentos" @click="typeFilter = 'all';">
                        <i class="fas fa-layer-group"></i>
                        <span>Todos</span>
                    </button>
                    <button
class="filter-btn filter-nota" :class="{ 'active': typeFilter === 'nota-medica' }"
                        title="Filtrar solo notas médicas" @click="typeFilter = 'nota-medica';">
                        <i class="fas fa-file-medical"></i>
                        <span>Notas</span>
                    </button>
                    <button
class="filter-btn filter-receta" :class="{ 'active': typeFilter === 'receta' }"
                        title="Filtrar solo recetas" @click="typeFilter = 'receta';">
                        <i class="fas fa-prescription"></i>
                        <span>Recetas</span>
                    </button>
                    <button
class="filter-btn filter-reporte" :class="{ 'active': typeFilter === 'documento' }"
                        title="Filtrar solo reportes" @click="typeFilter = 'documento';">
                        <i class="fas fa-file-image"></i>
                        <span>Reportes</span>
                    </button>

                    <button
class="btn-sort" type="button"
                        :title="sortOrder === 'desc' ? 'Ordenar ascendente' : 'Ordenar descendente'"
                        @click="toggleSortOrder">
                        <i
                            :class="sortOrder === 'desc' ? 'fas fa-arrow-down-short-wide' : 'fas fa-arrow-up-wide-short'"></i>
                    </button>
                    <button class="btn-clear-filters" type="button" @click="clearAllFilters">
                        <i class="fas fa-broom"></i>
                    </button>
                    <div class="view-mode-toggle">
                        <button
class="view-mode-btn" :class="{ 'active': viewMode === 'list' }" title="Vista de lista"
                            @click="viewMode = 'list'">
                            <i class="fas fa-list"></i>
                        </button>
                        <button
class="view-mode-btn" :class="{ 'active': viewMode === 'kanban' }"
                            title="Vista de columnas" @click="viewMode = 'kanban'">
                            <i class="fas fa-columns"></i>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Vista Kanban -->
            <div v-if="viewMode === 'kanban'">
                <div v-if="historyLoading" class="timeline-item avanzado">
                    <div class="timeline-content avanzado">
                        <div class="timeline-header avanzado">
                            <span class="tipo">Cargando historial...</span>
                        </div>
                        <p class="resumen">Obteniendo notas, recetas y documentos del paciente.</p>
                    </div>
                </div>

                <div v-else-if="historyError" class="timeline-item avanzado">
                    <div class="timeline-content avanzado">
                        <div class="timeline-header avanzado">
                            <span class="tipo">Error al cargar</span>
                        </div>
                        <p class="resumen">No se pudo obtener el historial del paciente.</p>
                    </div>
                </div>

                <TimelineKanban
v-else :items="sortedHistoryItems" :patient-id="patientId" @item-moved="handleItemMoved"
                    @edit-item="editarEvento" @delete-item="handleDeleteItem" @refresh="refreshHistory" />
            </div>

            <!-- Vista de lista (original) -->
            <div v-else-if="viewMode === 'list'">
                <div v-if="historyLoading" class="timeline-item avanzado">
                    <div class="timeline-content avanzado">
                        <div class="timeline-header avanzado">
                            <span class="tipo">Cargando historial...</span>
                        </div>
                        <p class="resumen">Obteniendo notas, recetas y documentos del paciente.</p>
                    </div>
                </div>

                <div v-else-if="historyError" class="timeline-item avanzado">
                    <div class="timeline-content avanzado">
                        <div class="timeline-header avanzado">
                            <span class="tipo" style="color:#ef4444">Error al cargar</span>
                        </div>
                        <p class="resumen">Reintenta en unos segundos.</p>
                    </div>
                </div>

                <div v-else-if="sortedHistoryItems.length === 0" class="timeline-item avanzado">
                    <div class="timeline-content avanzado">
                        <div class="timeline-header avanzado">
                            <span class="tipo">Sin registros</span>
                        </div>
                        <p class="resumen">No hay notas, recetas o documentos registrados.</p>
                    </div>
                </div>

                <div
v-for="item in sortedHistoryItems" v-else :key="item.tipo + '-' + item.id"
                    class="timeline-item avanzado" :class="{ 'seleccionado': notaSeleccionadaId === item.id }"
                    @click="handleSeleccionarNota(item)">
                    <div class="item-badge" :class="`badge-${item.tipo}`">
                        <i :class="getTypeIcon(item.tipo)"></i>
                        <span>{{ mapTipo(item.tipo) }}</span>
                    </div>
                    <div class="timeline-content avanzado">
                        <div class="timeline-header avanzado">
                            <span class="fecha">{{ formatDateShort(item.created_at) }}</span>
                            <div class="timeline-actions">
                                <button class="edit-btn" title="Editar" @click.stop="editarEvento(item)">
                                    <i class="fas fa-pen"></i>
                                </button>
                                <button
class="delete-btn" title="Eliminar"
                                    @click.stop="eliminarEvento(item.id, item.tipo)">
                                    <i class="fas fa-trash"></i>
                                </button>
                            </div>
                        </div>
                        <p class="resumen">{{ item.titulo }}<span v-if="item.resumen"> — {{ item.resumen }}</span></p>
                    </div>
                </div>
            </div>

        </div>
    </main>
</template>

<script setup>
import { defineProps, defineEmits, ref, onMounted, onBeforeUnmount, onUnmounted, computed, watch, nextTick, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import api from '@/services/api';
import PatientHC from '../Forms/ClinicalHistory/PatientHC.vue';
import { useMedicalWorkspace } from '@/composables/useMedicalWorkspace';
import { usePatientHistory } from '@/composables/usePatientHistory';
import SearchFilterClose from '@/components/SearchFilterClose.vue';
import TimelineKanban from './TimelineKanban.vue';
import { patientEventBus } from '@/utils/eventBus';

const props = defineProps({
    notaSeleccionadaId: {
        type: Number,
        default: null
    },
    patientId: {
        type: [Number, String],
        required: true
    },
    filtroActual: {
        type: String,
        default: ''
    },
    filtroAvanzado: {
        type: Object,
        default: null
    }
});

const emit = defineEmits(['seleccionar-nota']);

const store = useStore();
const { proxy } = getCurrentInstance();

// Usar el composable para acceder al estado global
const {
    seleccionarNota,
    modoAccion,
    cerrarModoAccion,
    activarModoAccion,
    cargarDatosEnFormulario,
    limpiarFiltros: limpiarFiltrosGlobales
} = useMedicalWorkspace();

// ===== BÚSQUEDA EN HISTORIA CLÍNICA =====
const historiaSearch = ref('');
const showSearch = ref(false);
const historiaBodyRef = ref(null);

// ===== MODO DE VISTA =====
const viewMode = ref('kanban'); // 'list' o 'kanban' - Por defecto Kanban

// ===== FILTRO POR TIPO =====
const typeFilter = ref('all'); // 'all', 'nota-medica', 'receta', 'documento'
const normalizeType = (tipo) => {
    const t = (tipo || '').toString().toLowerCase();
    if (t.includes('nota')) return 'nota-medica';
    if (t.includes('receta')) return 'receta';
    if (t.includes('reporte') || t.includes('documento') || t.includes('doc')) return 'documento';
    return t;
};

const syncHistoryFilter = () => {
    const canon = normalizeType(typeFilter.value);
    const map = {
        'nota-medica': 'notes',
        'receta': 'prescriptions',
        'documento': 'documents',
        'all': 'all'
    };
    setHistoryTypeFilter(map[canon] || 'all');
};

// ===== GENERACIÓN DE PDF =====
const generatingPdf = ref(false);

// Ref para el componente PatientHC completo
const patientHCRef = ref(null);

// 🏥 Obtener usuario autenticado desde Vuex
const user = computed(() => store.state.auth.user);
const doctorSpecialties = computed(() => user.value?.doctor?.specialties || []);
const hasMultipleSpecialties = computed(() => doctorSpecialties.value.length > 1);
const activeSpecialtyId = ref(null);
const specialtyLabels = {
    1: 'Medicina General',
    2: 'Ginecología',
    3: 'Pediatría',
    4: 'Oftalmología',
    7: 'Odontología'
};

// Título dinámico de Historia Clínica según especialidad activa
const currentHistoryTitle = computed(() => {
    if (!activeSpecialtyId.value) {
        return 'Historia Clínica Completa';
    }

    const activeSpec = doctorSpecialties.value.find(s => Number(s.id) === Number(activeSpecialtyId.value));
    if (!activeSpec) {
        return 'Historia Clínica Completa';
    }

    const specialtyName = activeSpec.name || specialtyLabels[activeSpec.id];
    if (!specialtyName) {
        return 'Historia Clínica Completa';
    }

    // Generar nombre completo según la especialidad
    const titleMap = {
        'Medicina General': 'Historia Clínica General',
        'Ginecología': 'Historia Clínica Ginecológica',
        'Pediatría': 'Historia Clínica Pediátrica',
        'Oftalmología': 'Historia Clínica Oftalmológica',
        'Odontología': 'Historia Clínica Odontológica'
    };

    return titleMap[specialtyName] || `Historia Clínica de ${specialtyName}`;
});

const getSpecialtyLabel = (spec) => {
    return spec?.name || specialtyLabels[spec?.id] || `Especialidad ${spec?.id || ''}`;
};

const isSpecialtyActive = (id) => {
    const hasSpec = doctorSpecialties.value.some(s => Number(s.id) === Number(id));
    if (!hasSpec) return false;
    if (!hasMultipleSpecialties.value || activeSpecialtyId.value === null) return true;
    return Number(activeSpecialtyId.value) === Number(id);
};

// 🔍 Función para verificar si el médico tiene una especialidad específica por ID
const hasSpecialty = (specialtyId) => {
    if (!user.value) return false;
    if (user.value.doctor && Array.isArray(user.value.doctor.specialties)) {
        return user.value.doctor.specialties.some(s => Number(s.id) === Number(specialtyId));
    }
    return false;
};

watch(doctorSpecialties, (list) => {
    if (!list || list.length === 0) {
        activeSpecialtyId.value = null;
        return;
    }
    if (!activeSpecialtyId.value || !list.some(s => Number(s.id) === Number(activeSpecialtyId.value))) {
        activeSpecialtyId.value = list[0].id;
    }
}, { immediate: true });

// Secciones disponibles en Historia Clínica (lista estática)
const availableSections = computed(() => {
    const sections = [];

    // Secciones generales (Medicina General y también visibles en Ginecología)
    if ((hasSpecialty(1) && isSpecialtyActive(1)) || (hasSpecialty(2) && isSpecialtyActive(2))) {
        sections.push(
            'Padecimientos Hereditarios',
            'Antecedentes No Patológicos',
            'Antecedentes Patológicos',
            'Padecimiento Actual',
            'Exploración Física',
            'Estudios de Laboratorio y Gabinete',
            'Diagnósticos CIE-10',
            'Plan y Tratamiento'
        );
    }

    // Secciones ginecológicas
    if (hasSpecialty(2) && isSpecialtyActive(2)) {
        sections.splice(3, 0, 'Ginecología y Obstetricia', 'Control de embarazo'); // después de Patológicos
    }

    return sections;
});

const toggleSearch = () => {
    showSearch.value = !showSearch.value;
    if (!showSearch.value) {
        clearHistoriaSearch();
    }
};

// Función para navegar a una sección específica
const navigateToSection = (sectionName) => {
    if (!patientHCRef.value) {
        console.warn('⚠️ PatientHC no está montado');
        return;
    }

    // Buscar el elemento por data-section-title o por el texto del título
    const container = document.querySelector('.historia-clinica-container');
    if (!container) return;

    const sectionMap = {
        'Padecimientos Hereditarios': 'section-hereditarios',
        'Antecedentes No Patológicos': 'section-no-patologicos',
        'Antecedentes Patológicos': 'section-patologicos',
        'Padecimiento Actual': 'section-padecimiento',
        'Exploración Física': 'section-exploracion',
        'Estudios de Laboratorio y Gabinete': 'section-estudios',
        'Diagnósticos CIE-10': 'section-diagnosticos',
        'Plan y Tratamiento': 'section-plan',
        'Ginecología y Obstetricia': 'section-ginecologia',
        'Control de embarazo': 'section-control-embarazo'
    };

    nextTick(() => {
        let targetSection = null;
        const targetId = sectionMap[sectionName];

        if (targetId) {
            targetSection = document.getElementById(targetId);
        }

        // Fallback: Buscar por texto si no se encuentra por ID
        if (!targetSection) {
            const sections = container.querySelectorAll('h3, h4, h5');
            targetSection = Array.from(sections).find(s =>
                s.textContent.toLowerCase().includes(sectionName.toLowerCase())
            );
        }

        if (targetSection) {
            const offset = 160; // Header + search wrapper + margen
            const elementPosition = targetSection.offsetTop;

            container.scrollTo({
                top: elementPosition - offset,
                behavior: 'smooth'
            });

            showSectionDropdown.value = false;
        } else {
            console.warn(`⚠️ No se encontró la sección: ${sectionName}`);
        }
    });
};

// Filtro de búsqueda para secciones
const filteredSections = computed(() => {
    if (!historiaSearch.value || historiaSearch.value.trim() === '') {
        return availableSections.value;
    }

    const searchLower = historiaSearch.value.toLowerCase().trim();

    // Normalizar texto (quitar acentos para búsqueda más flexible)
    const normalizeText = (text) => {
        return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    };

    const searchNormalized = normalizeText(searchLower);

    return availableSections.value.filter(section => {
        const sectionNormalized = normalizeText(section);
        return sectionNormalized.includes(searchNormalized);
    });
});

// Mostrar/ocultar el dropdown de secciones
const showSectionDropdown = ref(false);

const clearHistoriaSearch = () => {
    historiaSearch.value = '';
    showSectionDropdown.value = false;
};

// Watch para buscar cuando el usuario escriba
watch(historiaSearch, (searchTerm) => {
    // Mostrar dropdown siempre que haya focus en el campo
    // No importa si está vacío o no
    if (searchTerm !== null && searchTerm !== undefined) {
        showSectionDropdown.value = true;
    }
});

// Cerrar dropdown cuando se hace clic fuera
const handleClickOutside = (event) => {
    const searchContainer = document.querySelector('.historia-search');
    if (searchContainer && !searchContainer.contains(event.target)) {
        showSectionDropdown.value = false;
    }
};

onMounted(() => {
    document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
});

const handleSeleccionarNota = (item) => {
    // Scroll suave a la barra de navegación en la parte superior
    const barraFiltros = document.querySelector('.barra-filtros-flotante');
    if (barraFiltros) {
        barraFiltros.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
        // Fallback: scroll al inicio de la página
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // Si viene del timeline (tiene tipo y raw), editar directamente
    if (item.tipo && item.raw) {
        editarEvento(item);
    } else {
        // Legacy: para compatibilidad con llamadas antiguas
        emit('seleccionar-nota', item);
        seleccionarNota(item);
    }
};

const cerrarHistoriaClinica = () => {
    cerrarModoAccion();
};

// 📄 Función para generar PDF de Historia Clínica
const generateClinicalHistoryPdf = async () => {
    if (generatingPdf.value) return;

    try {
        generatingPdf.value = true;

        // Detectar qué especialidad está activa
        let endpoint = '/generate-clinical-history-pdf'; // Por defecto, Historia Clínica General
        let specialtyName = 'Historia Clínica';

        // Si hay especialidad activa específica
        if (activeSpecialtyId.value) {
            const activeId = Number(activeSpecialtyId.value);

            // Pediatría (id: 3)
            if (activeId === 3) {
                endpoint = '/generate-pediatric-history-pdf';
                specialtyName = 'Historia Clínica Pediátrica';
            }
            // Oftalmología (id: 4)
            else if (activeId === 4) {
                endpoint = '/generate-ophthalmic-history-pdf';
                specialtyName = 'Historia Clínica Oftalmológica';
            }
            // Odontología (id: 7)
            else if (activeId === 7) {
                endpoint = '/generate-dentistry-history-pdf';
                specialtyName = 'Historia Clínica Odontológica';
            }
        }

        const response = await api.post(endpoint, {
            patient_id: props.patientId
        });

        console.log('📄 Respuesta del servidor:', response.data);

        // El servidor puede devolver el PDF en diferentes formatos
        let pdfUrl = null;
        let pdfBase64 = null;

        // Formato 1: PDF en base64 (response.data.pdf)
        if (response.data?.pdf) {
            pdfBase64 = response.data.pdf;
        }
        // Formato 2: URL del PDF (response.data.data.pdf_url)
        else if (response.data?.data?.pdf_url) {
            pdfUrl = response.data.data.pdf_url;
        }
        // Formato 3: response.data.pdf_url
        else if (response.data?.pdf_url) {
            pdfUrl = response.data.pdf_url;
        }
        // Formato 4: response.data.url
        else if (response.data?.url) {
            pdfUrl = response.data.url;
        }

        // Si tenemos el PDF en base64, convertirlo a blob y descargarlo
        if (pdfBase64) {
            try {
                // Decodificar base64
                const binaryString = window.atob(pdfBase64);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }

                // Crear blob y URL
                const blob = new Blob([bytes], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);

                console.log('🔗 Abriendo PDF desde base64');

                // Abrir en nueva pestaña
                window.open(url, '_blank');

                // Limpiar URL después de un tiempo
                setTimeout(() => window.URL.revokeObjectURL(url), 60000);

                // Notificación de éxito
                proxy.$notify(
                    response.data.message || `${specialtyName} generada exitosamente`,
                    '',
                    'success',
                    'ni ni-like-2 ni-lg'
                );
            } catch (decodeError) {
                console.error('❌ Error al decodificar PDF base64:', decodeError);
                throw new Error('Error al procesar el PDF recibido');
            }
        }
        // Si tenemos una URL, abrirla directamente
        else if (pdfUrl) {
            // Agregar timestamp para evitar caché del navegador
            const urlWithTimestamp = pdfUrl.includes('?')
                ? `${pdfUrl}&t=${Date.now()}`
                : `${pdfUrl}?t=${Date.now()}`;

            console.log('🔗 Abriendo PDF desde URL:', urlWithTimestamp);

            // Abrir PDF en nueva pestaña
            window.open(urlWithTimestamp, '_blank');

            // Notificación de éxito
            proxy.$notify(
                response.data.message || `${specialtyName} generada exitosamente`,
                '',
                'success',
                'ni ni-like-2 ni-lg'
            );
        } else {
            console.error('❌ Estructura de respuesta no reconocida:', response.data);
            throw new Error('Respuesta inválida del servidor. El PDF no se encuentra en la respuesta.');
        }

    } catch (error) {
        console.error('❌ Error al generar PDF de Historia Clínica:', error);

        const errorMessage = error.response?.data?.message ||
            error.message ||
            'No se pudo generar el PDF. Intenta nuevamente.';

        proxy.$notify(
            errorMessage,
            '',
            'danger',
            'ni ni-fat-remove ni-lg'
        );
    } finally {
        generatingPdf.value = false;
    }
};

const editarEvento = (item) => {
    const barraFiltros = document.querySelector('.barra-filtros-flotante');
    if (barraFiltros) {
        barraFiltros.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // Mapear el tipo del timeline al modo de acción correspondiente
    const modoMap = {
        'nota-medica': 'editar-nota',
        'receta': 'editar-receta',
        'documento': 'editar-documento'
    };

    const resourceTypeMap = {
        'nota-medica': 'nota',
        'receta': 'receta',
        'documento': 'documento'
    };

    const modo = modoMap[item.tipo];
    const resourceType = resourceTypeMap[item.tipo];

    if (modo) {
        if (!item.raw) {
            console.error('❌ item.raw está vacío o undefined:', item);
            return;
        }

        activarModoAccion(modo, item.titulo || 'Editar', {
            editingState: {
                currentId: item.id,
                resourceType: resourceType
            }
        });

        cargarDatosEnFormulario(item.raw, item.tipo);
    } else {
        console.warn('⚠️ No se encontró modo para tipo:', item.tipo);
    }
};

const eliminarEvento = async (itemId, itemTipo) => {
    const item = historyItems.value.find(i => i.id === itemId && i.tipo === itemTipo);
    if (item) {
        await deleteItem(item);
    }
};

const handleDeleteItem = async (item) => {
    await eliminarEvento(item.id, item.tipo);
};

const data = ref({
    non_pathological: null,
    pathological: null,
    current_condition: null,
    diagnostics: null,
    physical_examinations: null,
    gynecology: null
});

const fullPatientData = ref(null);

const patientRecord = computed(() => {
    return fullPatientData.value || { id: props.patientId };
});

const loadData = async () => {
    if (!props.patientId) return;
    try {
        const response = await api.get(`/pacientes/${props.patientId}`);
        data.value = response.data.data;
        fullPatientData.value = response.data.data;
    } catch (error) {
        void error;
    }
};

onMounted(() => {
    loadData();
    // Cargar historial al montar
    refreshHistory();

    // Suscribirse a cambios en el historial del paciente (notas, recetas, documentos)
    const unsubscribe = patientEventBus.subscribe(props.patientId, (eventData) => {
        // Si es una actualización de datos del paciente, recargar todo
        if (eventData?.action === 'patient-data-updated' || eventData?.id === props.patientId) {
            loadData();
        }
        // Siempre refrescar el historial
        refreshHistory();
    });

    // Limpiar suscripción al desmontar
    onBeforeUnmount(() => {
        unsubscribe();
    });
});


watch(() => props.patientId, () => {
    data.value = {};
    loadData();
    refreshHistory();
});

const {
    loading: historyLoading,
    error: historyError,
    filteredItems: historyItems,
    refresh: refreshHistory,
    setSearchQuery,
    setDateFilter,
    setFilter: setHistoryTypeFilter,
    deleteItem
} = usePatientHistory(() => props.patientId, { perPage: 100 });

const handleItemMoved = () => {
    // Refrescar historial si el kanban emite movimiento
    refreshHistory();
};

watch(() => props.filtroAvanzado, (newVal) => {
    if (!newVal || !newVal.tipo) {
        setDateFilter({ type: 'all' });
        return;
    }

    if (newVal.tipo === 'mes' && newVal.mes) {
        setDateFilter({
            type: 'month',
            monthInput: newVal.mes, // formato YYYY-MM
            exactDate: '',
            weekDate: '',
            startDate: '',
            endDate: ''
        });
    } else if (newVal.tipo === 'fecha' && newVal.inicio && newVal.fin) {
        setDateFilter({
            type: 'range',
            startDate: newVal.inicio,
            endDate: newVal.fin,
            exactDate: '',
            weekDate: '',
            monthInput: ''
        });
    } else if (newVal.tipo === 'recientes') {
        const hoy = new Date();
        hoy.setHours(23, 59, 59, 999);
        const hace7Dias = new Date(hoy);
        hace7Dias.setDate(hoy.getDate() - 6);
        hace7Dias.setHours(0, 0, 0, 0);

        const formatDate = (d) => {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
        };

        const inicio = formatDate(hace7Dias);
        const fin = formatDate(hoy);

        setDateFilter({
            type: 'range',
            startDate: inicio,
            endDate: fin,
            exactDate: '',
            weekDate: '',
            monthInput: ''
        });
    } else {
        // Si el tipo no es reconocido o falta información, mostrar todos
        setDateFilter({ type: 'all' });
    }
}, { deep: true, immediate: true });

// Estado local para filtros
const historySearch = ref('');
const dateMode = ref('all');
const exactDate = ref('');
const monthDate = ref('');
const monthInput = ref('');
const rangeDates = ref({ start: '', end: '' });
const sortOrder = ref('desc'); // 'desc' para descendente, 'asc' para ascendente

// Función para calcular los últimos 7 días (incluyendo hoy)
const getLast7Days = () => {
    const hoy = new Date();
    hoy.setHours(23, 59, 59, 999); // Final del día de hoy

    const hace7Dias = new Date(hoy);
    hace7Dias.setDate(hoy.getDate() - 6); // -6 porque incluimos el día de hoy (hoy + 6 días atrás = 7 días totales)
    hace7Dias.setHours(0, 0, 0, 0); // Inicio del día hace 7 días

    const fmt = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    return {
        start: fmt(hace7Dias),
        end: fmt(hoy)
    };
};

const applyDateFilter = () => {
    if (dateMode.value === 'week') {
        const last7Days = getLast7Days();
        setDateFilter({
            type: 'range',
            startDate: last7Days.start,
            endDate: last7Days.end,
            exactDate: '',
            weekDate: '',
            monthInput: ''
        });
    } else {
        setDateFilter({
            type: dateMode.value,
            exactDate: exactDate.value,
            monthInput: monthInput.value,
            startDate: rangeDates.value.start,
            endDate: rangeDates.value.end
        });
    }
};

const clearAllFilters = () => {
    historySearch.value = '';
    dateMode.value = 'all';
    exactDate.value = '';
    monthDate.value = '';
    monthInput.value = '';
    rangeDates.value = { start: '', end: '' };
    sortOrder.value = 'desc';
    typeFilter.value = 'all';
    syncHistoryFilter();
    setSearchQuery('');
    setDateFilter({ type: 'all' });
    // Limpiar también el filtro avanzado global
    limpiarFiltrosGlobales();
};

const toggleSortOrder = () => {
    sortOrder.value = sortOrder.value === 'desc' ? 'asc' : 'desc';
};

// Reaccionar a cambios para aplicar filtros automáticamente
watch(historySearch, (v) => setSearchQuery(v));

// Watch para derivar monthInput desde monthDate
watch(monthDate, (val) => {
    if (val) {
        // El valor viene en formato YYYY-MM desde el monthSelect plugin
        monthInput.value = val;
    } else {
        monthInput.value = '';
    }
});

watch([dateMode, exactDate, monthInput, rangeDates], () => {
    applyDateFilter();
}, { deep: true });

watch(typeFilter, () => {
    syncHistoryFilter();
});

// Computed para ordenar y filtrar los items según sortOrder y filtros avanzados
// NOTA: El filtro de FECHA lo maneja usePatientHistory a través de setDateFilter
// Este computed solo filtra por TIPO DE DOCUMENTO y ORDENA
const sortedHistoryItems = computed(() => {
    let items = [...historyItems.value]; // historyItems ya viene filtrado por fecha desde usePatientHistory

    // APLICAR FILTRO POR TIPO DE DOCUMENTO
    // Prioridad: typeFilter (botones UI) > filtroAvanzado > filtroActual
    if (typeFilter.value !== 'all') {
        // Filtrar por el tipo seleccionado en los botones de la UI
        const filtroCanonico = normalizeType(typeFilter.value);
        items = items.filter(item => normalizeType(item.tipo) === filtroCanonico);
    } else {
        // Si no hay filtro en botones, usar filtros antiguos por compatibilidad
        const filtroTipo = props.filtroAvanzado?.documento || props.filtroActual;

        if (filtroTipo && filtroTipo !== 'todo') {
            // Mapear el tipo de documento del filtro al tipo interno
            const tipoMap = {
                'nota-medica': 'nota-medica',
                'nota': 'nota-medica',
                'receta': 'receta',
                'reporte': 'documento',  // 'reporte' en el filtro -> 'documento' en el timeline
                'documento': 'documento',
                'signos-vitales': 'signos-vitales',
                'alertas': 'alertas',
                'alergias': 'alergias'
            };

            const tipoFiltrado = tipoMap[filtroTipo];

            if (tipoFiltrado) {
                items = items.filter(item => normalizeType(item.tipo) === normalizeType(tipoFiltrado));
            }
        }
    }

    // ORDENAR POR FECHA
    items.sort((a, b) => {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);

        if (sortOrder.value === 'desc') {
            return dateB - dateA; // Más reciente primero
        } else {
            return dateA - dateB; // Más antiguo primero
        }
    });

    return items;
});

const mapTipo = (tipo) => {
    const tipoCanonico = normalizeType(tipo);
    if (tipoCanonico === 'nota-medica') return 'Nota Médica';
    if (tipoCanonico === 'receta') return 'Receta';
    if (tipoCanonico === 'documento') return 'Reporte';
    return 'Registro';
};

const getTypeIcon = (tipo) => {
    const tipoCanonico = normalizeType(tipo);
    if (tipoCanonico === 'nota-medica') return 'fas fa-file-medical';
    if (tipoCanonico === 'receta') return 'fas fa-prescription';
    if (tipoCanonico === 'documento') return 'fas fa-file-image';
    return 'fas fa-file';
};

const formatDateShort = (d) => {
    if (!d) return '';
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: '2-digit' });
};
</script>

<style scoped>
.linea-historia.avanzado {
    overflow-y: auto;
    overflow-x: hidden;
    height: 100%;
    padding: 0.5rem;
    background: linear-gradient(135deg, #f8f9fe 0%, #eef2f7 100%);
    scroll-behavior: smooth;
}

.specialty-tabs {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    padding: 6px 0 2px;
}

.specialty-tab-btn {
    border: 1px solid #e2e8f0;
    background: #fff;
    color: #475569;
    border-radius: 10px;
    padding: 8px 12px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    box-shadow: 0 4px 10px rgba(15, 23, 42, 0.06);
    transition: all 0.2s ease;
}

.specialty-tab-btn.active {
    background: linear-gradient(135deg, #2563eb, #7c3aed);
    color: #fff;
    border-color: transparent;
    box-shadow: 0 8px 20px rgba(99, 102, 241, 0.25);
}

.specialty-tab-btn:hover {
    transform: translateY(-1px);
}

.linea-historia.avanzado.sin-scroll {
    overflow-y: hidden;
}

/* Scrollbar mejorado */
.linea-historia.avanzado::-webkit-scrollbar {
    width: 10px;
}

.linea-historia.avanzado::-webkit-scrollbar-track {
    background: rgba(102, 126, 234, 0.05);
    border-radius: 10px;
    margin: 4px 0;
}

.linea-historia.avanzado::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 10px;
    border: 2px solid transparent;
    background-clip: padding-box;
}

.linea-historia.avanzado::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #5568d3 0%, #65398b 100%);
    border-radius: 10px;
}

.timeline-container.avanzado {
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
    padding: 0.5rem 0.25rem;
}

/* Filtros modernos */
.timeline-filters.avanzado {
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%);
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 12px;
    margin-bottom: 6px;
}

.filters-row {
    width: 100%;
    display: grid;
    gap: 12px;
    align-items: center;
}

.filters-top {
    grid-template-columns: 1fr auto;
    gap: 12px;
}

.filters-all-in-one {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: nowrap;
}

.filters-all-in-one .filter-search {
    flex: 1;
    min-width: 200px;
}

.filters-single {
    display: grid;
    grid-template-columns: 1fr auto auto;
    gap: 12px;
    align-items: center;
}

.filters-bottom {
    grid-template-columns: 1fr;
    margin-top: 12px;
}

.filter-search {
    min-width: 0;
}

.date-filters {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.filter-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex-wrap: nowrap;
    min-width: 0;
}

@media (max-width: 1280px) {
    .filters-top {
        grid-template-columns: 1fr;
        gap: 10px;
    }

    .filters-single {
        grid-template-columns: 1fr;
        gap: 10px;
    }

    .filters-all-in-one {
        flex-wrap: wrap;
    }

    .filters-all-in-one .filter-search {
        flex: 1 1 100%;
        min-width: 100%;
    }

    .filter-actions {
        justify-content: flex-start;
    }

    .type-filter-buttons {
        flex-wrap: wrap;
        justify-content: flex-start;
    }
}

@media (max-width: 768px) {
    .filter-search {
        min-width: 0;
    }

    .filters-row {
        gap: 10px;
    }

    .filter-actions {
        flex-wrap: wrap;
    }

    .type-filter-buttons {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 8px;
    }

    .filters-all-in-one .filter-btn {
        flex: 1 1 calc(50% - 4px);
    }
}

.timeline-select {
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    padding: 8px 10px;
    background: #fff;
    color: #334155;
}

.timeline-date :deep(.form-control) {
    border-radius: 10px !important;
    border: 1.5px solid #e2e8f0 !important;
}

/* Forzar tamaños fijos para evitar saltos visuales */
.timeline-filters.avanzado :deep(.enhanced-search-container) {
    min-height: 44px;
}

.timeline-filters.avanzado :deep(.search-input-wrapper) {
    min-height: 44px;
}

.timeline-filters.avanzado :deep(.search-filter-input) {
    height: 44px;
}

.timeline-select {
    height: 44px;
}

.timeline-date :deep(.flatpickr-input) {
    height: 44px !important;
    min-width: 180px;
}

.date-mode-wrapper {
    min-width: 240px;
}

.timeline-filters.avanzado :deep(.choices) {
    min-width: 220px;
}

.timeline-filters.avanzado :deep(.choices__inner) {
    min-height: 44px;
    height: 44px;
    display: flex;
    align-items: center;
}

.timeline-filters.avanzado :deep(.choices__list--dropdown) {
    z-index: 10060;
}

.timeline-filters.avanzado :deep(.flatpickr-calendar) {
    z-index: 10050 !important;
}

/* Estilos base para todos los botones de control */
.btn-clear-filters,
.btn-sort {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border-radius: 10px;
    border: 1px solid transparent;
    background: white;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
    color: #64748b;
    cursor: pointer;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    height: 40px;
    width: 40px;
    font-weight: 600;
    font-size: 0.95rem;
    position: relative;
    overflow: hidden;
}

.btn-clear-filters::before,
.btn-sort::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    transition: opacity 0.25s ease;
}

.btn-clear-filters {
    border-color: #f1f5f9;
}

.btn-clear-filters::before {
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
}

.btn-clear-filters:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.15);
    border-color: #667eea;
}

.btn-clear-filters:hover::before {
    opacity: 1;
}

.btn-sort {
    border-color: #f1f5f9;
}

.btn-sort::before {
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.05) 100%);
}

.btn-sort:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(16, 185, 129, 0.15);
    border-color: #10b981;
}

.btn-sort:hover::before {
    opacity: 1;
}

.btn-sort i {
    color: #10b981;
}

.btn-clear-filters i {
    color: #667eea;
}

.btn-sort i,
.btn-clear-filters i {
    font-size: 1rem;
    transition: transform 0.25s ease;
}

.btn-sort:hover i,
.btn-clear-filters:hover i {
    transform: scale(1.1);
}

.view-mode-toggle {
    display: flex;
    gap: 4px;
    background: #f1f5f9;
    padding: 4px;
    border-radius: 10px;
    border: 1px solid #e2e8f0;
}

.view-mode-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    border: none;
    background: transparent;
    color: #cbd5e1;
    cursor: pointer;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    font-size: 1rem;
    font-weight: 600;
    position: relative;
}

.view-mode-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(148, 163, 184, 0.1);
    opacity: 0;
    border-radius: 10px;
    transition: opacity 0.25s ease;
}

.view-mode-btn i {
    color: #94a3b8;
    font-size: 0.95rem;
    transition: all 0.25s ease;
    position: relative;
    z-index: 1;
}

.view-mode-btn:hover i {
    color: #667eea;
}

.view-mode-btn:hover::before {
    opacity: 1;
}

.view-mode-btn.active {
    background: white;
    color: #667eea;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.15);
}

.view-mode-btn.active::before {
    opacity: 0;
}

.view-mode-btn.active i {
    color: #667eea;
    transform: scale(1.1);
}

.type-filter-buttons {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: nowrap;
}

.filter-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.65rem 1rem;
    white-space: nowrap;
    justify-content: center;
    border-radius: 14px;
    font-weight: 600;
    font-size: 0.85rem;
    border: 1px solid #f1f5f9;
    background: white;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
    color: #475569;
    cursor: pointer;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
}

.filter-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%);
    opacity: 0;
    transition: opacity 0.25s ease;
}

.filter-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.15);
}

.filter-btn:hover::before {
    opacity: 1;
}

.filter-btn.active {
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.12);
    transform: translateY(-1px) scale(1.02);
    border-color: #d1d5db;
}

.filter-btn i {
    font-size: 0.95rem;
    transition: transform 0.25s ease;
}

.filter-btn.active i {
    transform: scale(1.1);
}

.filter-nota {
    color: #3b82f6;
}

.filter-nota:hover {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(37, 99, 235, 0.08) 100%);
}

.filter-nota.active {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
    border-color: #3b82f6;
}

.filter-receta {
    color: #22c55e;
}

.filter-receta:hover {
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.08) 0%, rgba(22, 163, 74, 0.08) 100%);
}

.filter-receta.active {
    background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
    color: white;
    border-color: #22c55e;
}

.filter-reporte {
    color: #ec4899;
}

.filter-reporte:hover {
    background: linear-gradient(135deg, rgba(236, 72, 153, 0.08) 0%, rgba(219, 39, 119, 0.08) 100%);
}

.filter-reporte.active {
    background: linear-gradient(135deg, #ec4899 0%, #db2777 100%);
    color: white;
    border-color: #ec4899;
}

.timeline-item.avanzado {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 1.5rem 1.75rem;
    background: white;
    border-radius: 16px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border-left: 4px solid transparent;
    min-height: 85px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
    position: relative;
    overflow: hidden;
}

.item-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 700;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    flex-shrink: 0;
    min-width: fit-content;
}

.item-badge i {
    font-size: 14px;
}

.item-badge.badge-nota-medica {
    background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
    color: #1e40af;
    border: 1px solid #93c5fd;
}

.item-badge.badge-receta {
    background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
    color: #065f46;
    border: 1px solid #6ee7b7;
}

.item-badge.badge-documento {
    background: linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%);
    color: #9f1239;
    border: 1px solid #f9a8d4;
}

.timeline-item.avanzado::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    opacity: 0;
    transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.timeline-item.avanzado:hover {
    transform: translateX(8px);
    box-shadow: 0 8px 28px rgba(102, 126, 234, 0.18);
}

.timeline-item.avanzado:hover::before {
    opacity: 1;
}

.timeline-item.avanzado.seleccionado {
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.08) 100%);
    border-left: 4px solid transparent;
    box-shadow: 0 10px 32px rgba(102, 126, 234, 0.25);
    transform: translateX(8px);
}

.timeline-item.avanzado.seleccionado::before {
    opacity: 1;
    width: 6px;
}

.timeline-item.avanzado.seleccionado .item-badge {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    transform: scale(1.03);
}

.timeline-content.avanzado {
    flex-grow: 1;
    min-width: 0;
    padding-left: 0;
}

.timeline-header.avanzado {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.5rem;
    position: relative;
}

.timeline-header .tipo {
    font-weight: 700;
    font-size: 0.95rem;
    color: #2d3748;
    position: relative;
    padding-left: 1.5rem;
}

.timeline-header .tipo::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 8px;
    height: 8px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 50%;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.15);
}

.timeline-item.avanzado.seleccionado .timeline-header .tipo::before {
    animation: pulse-ring 1.5s ease-out infinite;
}

@keyframes pulse-ring {
    0% {
        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.15);
    }

    50% {
        box-shadow: 0 0 0 6px rgba(102, 126, 234, 0.3);
    }

    100% {
        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.15);
    }
}

.timeline-header .fecha {
    font-size: 0.85rem;
    color: #8492a6;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 0.35rem;
}

.timeline-header .fecha::before {
    content: '\f017';
    font-family: 'Font Awesome 6 Pro';
    font-weight: 400;
    font-size: 0.8rem;
    opacity: 0.7;
}

.timeline-actions {
    display: flex;
    gap: 0.5rem;
    margin-left: auto;
    opacity: 0;
    transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.timeline-item:hover .timeline-actions {
    opacity: 1;
}

.edit-btn,
.delete-btn {
    background: rgba(102, 126, 234, 0.08);
    border: none;
    color: #667eea;
    font-size: 0.9rem;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    padding: 0.5rem;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
}

.edit-btn:hover {
    background: rgba(102, 126, 234, 0.15);
    color: #5568d3;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.25);
}

.delete-btn {
    background: rgba(239, 68, 68, 0.08);
    color: #ef4444;
}

.delete-btn:hover {
    background: rgba(239, 68, 68, 0.15);
    color: #dc2626;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
}

.delete-btn:active,
.edit-btn:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.2);
}

.resumen {
    margin: 0;
    font-size: 0.92rem;
    color: #64748b;
    line-height: 1.6;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
}

.timeline-item.avanzado.seleccionado .resumen {
    color: #475569;
    font-weight: 500;
}

/* Animación de entrada */
.timeline-item.avanzado {
    animation: slideInFromLeft 0.4s cubic-bezier(0.4, 0, 0.2, 1) backwards;
    margin-bottom: 1.25rem;
}

.timeline-item.avanzado:nth-child(1) {
    animation-delay: 0.05s;
}

.timeline-item.avanzado:nth-child(2) {
    animation-delay: 0.1s;
}

.timeline-item.avanzado:nth-child(3) {
    animation-delay: 0.15s;
}

.timeline-item.avanzado:nth-child(4) {
    animation-delay: 0.2s;
}

.timeline-item.avanzado:nth-child(5) {
    animation-delay: 0.25s;
}

@keyframes slideInFromLeft {
    from {
        opacity: 0;
        transform: translateX(-20px);
    }

    to {
        opacity: 1;
        transform: translateX(0);
    }
}

/* ========================================
   VISTA HISTORIA CLÍNICA COMPLETA
   ======================================== */
.historia-clinica-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: white;
    overflow-y: auto;
    overflow-x: hidden;
}

.historia-clinica-container::-webkit-scrollbar {
    width: 10px;
}

.historia-clinica-container::-webkit-scrollbar-track {
    background: rgba(102, 126, 234, 0.05);
    border-radius: 10px;
}

.historia-clinica-container::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 10px;
    border: 2px solid transparent;
    background-clip: padding-box;
}

.historia-clinica-container::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #5568d3 0%, #65398b 100%);
}

.historia-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1.5rem;
    padding: 1.25rem 1.75rem;
    background: #ffffff;
    border-bottom: 2px solid rgba(102, 126, 234, 0.08);
    flex-shrink: 0;
    position: sticky;
    top: 0;
    z-index: 1000;
    height: 60px;
    min-height: 60px;
}

.historia-header h3 {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 700;
    color: #2d3748;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    white-space: nowrap;
}

.historia-header h3 i {
    color: #667eea;
    font-size: 1.2rem;
}

.historia-search-wrapper {
    padding: 20px 28px;
    background: #ffffff;
    border-bottom: 2px solid rgba(102, 126, 234, 0.08);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    flex-shrink: 0;
    position: sticky;
    top: 60px;
    z-index: 999;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 80px;
    min-height: 80px;
    max-height: 80px;
}

.historia-search {
    width: 100%;
    max-width: 500px;
    position: relative;
}

.historia-search :deep(.enhanced-search-container) {
    margin: 0 !important;
    padding: 0 !important;
    position: relative !important;
}

.historia-search :deep(.search-input-wrapper) {
    height: 40px !important;
    min-height: 40px !important;
    max-height: 40px !important;
    transform: none !important;
    position: relative !important;
    margin: 0 !important;
    padding: 0 !important;
}

.historia-search :deep(.search-filter-input) {
    height: 40px;
    border-radius: 10px;
    border: 1.5px solid #e2e8f0;
    background: white;
    font-size: 0.9rem;
}

.historia-search :deep(.search-filter-input:focus) {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* Dropdown de secciones */
.sections-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    right: 0;
    background: white;
    border-radius: 12px;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.1);
    border: 1.5px solid rgba(102, 126, 234, 0.3);
    z-index: 99999;
    overflow: hidden;
    max-height: 450px;
    display: flex;
    flex-direction: column;
}

.dropdown-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.dropdown-title {
    font-size: 0.85rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.close-dropdown {
    background: rgba(255, 255, 255, 0.2);
    border: none;
    width: 24px;
    height: 24px;
    border-radius: 6px;
    cursor: pointer;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
}

.close-dropdown:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
}

.sections-list {
    overflow-y: auto;
    max-height: 340px;
}

.sections-list::-webkit-scrollbar {
    width: 6px;
}

.sections-list::-webkit-scrollbar-track {
    background: rgba(102, 126, 234, 0.05);
}

.sections-list::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 10px;
}

.section-item {
    width: 100%;
    padding: 12px 16px;
    background: transparent;
    border: none;
    border-bottom: 1px solid rgba(102, 126, 234, 0.08);
    text-align: left;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 0.9rem;
    color: #2d3748;
    transition: all 0.2s ease;
}

.section-item:hover {
    background: rgba(102, 126, 234, 0.06);
    padding-left: 20px;
}

.section-item i {
    color: #667eea;
    font-size: 0.75rem;
    transition: transform 0.2s ease;
}

.section-item:hover i {
    transform: translateX(4px);
}

.section-item span {
    flex: 1;
    font-weight: 500;
}

/* Mensaje de no resultados */
.no-results {
    padding: 32px 24px;
    text-align: center;
    color: #718096;
}

.no-results i {
    font-size: 2rem;
    color: #cbd5e0;
    margin-bottom: 12px;
}

.no-results p {
    margin: 0;
    font-size: 0.9rem;
    font-weight: 500;
}

/* Animación del dropdown */
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.dropdown-fade-enter-from {
    opacity: 0;
    transform: translateY(-8px);
}

.dropdown-fade-leave-to {
    opacity: 0;
    transform: translateY(8px);
}

.search-toggle-btn {
    background: transparent;
    border: none;
    color: #6b7280;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    margin-right: 0.5rem;
}

.search-toggle-btn:hover {
    background-color: #f3f4f6;
    color: #4f46e5;
}

.search-toggle-btn.active {
    background-color: #eef2ff;
    color: #4f46e5;
}

.pdf-btn {
    background: transparent;
    border: none;
    color: #6b7280;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    margin-right: 0.5rem;
}

.pdf-btn:hover:not(:disabled) {
    background-color: #fef3c7;
    color: #d97706;
}

.pdf-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.pdf-btn.loading {
    background-color: #fef3c7;
    color: #d97706;
}

.cerrar-btn {
    background: #fee2e2;
    border: none;
    color: #ef4444;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    font-size: 1.5rem;
    line-height: 1;
    padding-bottom: 4px;
    /* Ajuste visual para el caracter times */
}

.cerrar-btn:hover {
    background: #fecaca;
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.2);
}

.header-buttons {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}
</style>
